import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { useTheme } from "@/contexts/ThemeContext";
import { useJobCommsWebSocket } from "@/contexts/JobCommsWebSocketContext";
import {
  MapPin, MessageSquare, FileText, ChevronRight,
  Loader2, Briefcase, Search
} from "lucide-react";
import "@/styles/commandDeck.css";

// Command Deck status badge styling (color / bg / leading dot)
const cdStatusBadge = {
  HIRED: { color: "#22D3EE", bg: "rgba(14,116,144,.25)", dot: true },
  PARTIALLY_HIRED: { color: "#67C6B4", bg: "rgba(103,198,180,.18)", dot: false },
  PENDING_COMPLETION: { color: "#FCD34D", bg: "rgba(252,211,77,.16)", dot: false },
  RELEASE_PAYMENT: { color: "#60A5FA", bg: "rgba(96,165,250,.18)", dot: false },
  PAID_COMPLETED: { color: "#67C6B4", bg: "rgba(103,198,180,.16)", dot: false },
};

const statusColors = {
  HIRED: "bg-blue-500",
  PARTIALLY_HIRED: "bg-blue-400",
  PENDING_COMPLETION: "bg-purple-500",
  RELEASE_PAYMENT: "bg-green-500",
  PAID_COMPLETED: "bg-emerald-600",
};

const statusLabels = {
  HIRED: "Confirmed",
  PARTIALLY_HIRED: "Partially Hired",
  PENDING_COMPLETION: "Pending Completion",
  RELEASE_PAYMENT: "Awaiting Payment",
  PAID_COMPLETED: "Completed",
};

const upcomingStatuses = ["HIRED", "PARTIALLY_HIRED", "PENDING_COMPLETION", "RELEASE_PAYMENT"];
const completedStatuses = ["PAID_COMPLETED"];

const formatDate = (dateStr) => {
  if (!dateStr) return { dayOfWeek: "", fullDate: "TBD" };
  try {
    const d = new Date(dateStr + "T00:00:00");
    return {
      dayOfWeek: d.toLocaleDateString("en-AU", { weekday: "short" }),
      fullDate: d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }),
    };
  } catch {
    return { dayOfWeek: "", fullDate: dateStr };
  }
};

const getDaysUntil = (dateStr) => {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const jobDate = new Date(dateStr + "T00:00:00");
  return Math.ceil((jobDate - today) / (1000 * 60 * 60 * 24));
};

const timeAgo = (timestamp) => {
  if (!timestamp) return "";
  const now = new Date();
  const t = new Date(timestamp);
  const diffMs = now - t;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return t.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
};

const JobComms = ({ onLogout }) => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const ws = useJobCommsWebSocket();
  const user = JSON.parse(localStorage.getItem("shootzu_user") || "{}");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("upcoming");

  const fetchJobs = useCallback(async () => {
    if (!user.email) return;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/job-comms?user_email=${user.email}`
      );
      setJobs(res.data.jobs || []);
    } catch (err) {
      console.error("Failed to load job comms:", err);
    } finally {
      setLoading(false);
    }
  }, [user.email]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    if (!ws) return;
    const unsub = ws.on("new_message", () => fetchJobs());
    return unsub;
  }, [ws, fetchJobs]);

  const filteredJobs = jobs
    .filter((job) => {
      // Filter by upcoming/completed
      const statusList = activeFilter === "completed" ? completedStatuses : upcomingStatuses;
      if (!statusList.includes(job.status)) return false;

      // Then filter by search
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (
        job.title?.toLowerCase().includes(q) ||
        job.other_party_name?.toLowerCase().includes(q) ||
        job.location_address?.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      // Completed: most recent first. Upcoming: soonest first.
      const dateA = a.date || "";
      const dateB = b.date || "";
      return activeFilter === "completed"
        ? dateB.localeCompare(dateA)
        : dateA.localeCompare(dateB);
    });

  const totalUnread = jobs.reduce((sum, j) => sum + (j.unread_count || 0), 0);

  return (
    <div className="min-h-screen" style={{ background: "#0A0E1A" }}>
      <Sidebar onLogout={onLogout} />
      <Header />

      <div className="cd-root sidebar-content pt-16 lg:pt-20">
        {/* Animated bloom blobs */}
        <div className="cd-bloom-wrap" aria-hidden="true">
          <div className="cd-bloom cd-bloom-1" />
          <div className="cd-bloom cd-bloom-2" />
        </div>

        <div className="cd-main" style={{ maxWidth: 1120 }}>
          {/* Header */}
          <div className="cd-col-mobile" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 20, marginBottom: 22 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <span className="cd-display" style={{ fontWeight: 700, fontSize: 11, letterSpacing: 2, color: "#67C6B4" }}>JOB COMMS</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontWeight: 700, fontSize: 11.5, color: "#67C6B4", background: "rgba(103,198,180,.16)", padding: "4px 11px", borderRadius: 999 }}>
                  {filteredJobs.length} {filteredJobs.length === 1 ? "job" : "jobs"}
                </span>
                {totalUnread > 0 && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 700, fontSize: 11.5, color: "#C98AD4", background: "rgba(120,40,124,.25)", padding: "4px 11px", borderRadius: 999 }}>
                    {totalUnread} unread
                  </span>
                )}
              </div>
              <div className="cd-display" style={{ fontWeight: 800, fontSize: 32, letterSpacing: "-1px", color: "#fff", marginTop: 8 }}>Confirmed jobs</div>
              <div style={{ fontSize: 14, color: "rgba(255,255,255,.55)", marginTop: 5 }}>
                {activeFilter === "completed"
                  ? "Your completed jobs — chat, docs and payment history in one place."
                  : "Coordinate every locked-in shoot — chat, docs and payment in one place."}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flex: "0 0 auto" }}>
              <button
                type="button"
                aria-label="Notifications"
                style={{ width: 46, height: 46, borderRadius: 13, background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.1)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", position: "relative" }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.94 1.94 0 0 0 3.4 0" /></svg>
                {totalUnread > 0 && (
                  <span style={{ position: "absolute", top: 11, right: 12, width: 8, height: 8, borderRadius: 999, background: "#28C0D0", border: "2px solid #0C1120" }} />
                )}
              </button>
            </div>
          </div>

          {/* Tabs + Search */}
          <div className="cd-col-mobile" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 18, flexWrap: "wrap" }}>
            <div className="cd-tabs">
              {[
                { key: "upcoming", label: "Upcoming" },
                { key: "completed", label: "Completed" },
              ].map((tab) => {
                const count = jobs.filter((j) => (tab.key === "completed" ? completedStatuses : upcomingStatuses).includes(j.status)).length;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveFilter(tab.key)}
                    className={`cd-tab ${activeFilter === tab.key ? "cd-tab-active" : ""}`}
                  >
                    {tab.label}
                    {count > 0 && <span className="cd-tab-count">{count}</span>}
                  </button>
                );
              })}
            </div>
            <div style={{ position: "relative", flex: 1, minWidth: 240, maxWidth: 380 }}>
              <Search style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", width: 17, height: 17, color: "#A8AEBC" }} />
              <input
                type="text"
                className="cd-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search jobs, crew, locations..."
                style={{ padding: "11px 14px 11px 40px" }}
              />
            </div>
          </div>

          {/* Job Cards */}
          {loading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 11, padding: "56px 24px" }}>
                <Loader2 className="animate-spin" style={{ width: 24, height: 24, color: "rgba(40,192,208,.85)" }} />
                <span style={{ fontSize: 13.5, color: "rgba(255,255,255,.55)" }}>Loading confirmed jobs…</span>
              </div>
              {[0, 1].map((i) => (
                <div key={i} className="cd-skel" style={{ height: 96 }} />
              ))}
            </div>
          ) : filteredJobs.length === 0 ? (
            <div style={{ background: "rgba(255,255,255,.045)", border: "1px solid rgba(255,255,255,.09)", borderRadius: 16, padding: "64px 24px", textAlign: "center" }}>
              <Briefcase style={{ width: 56, height: 56, margin: "0 auto 16px", color: "rgba(255,255,255,.3)" }} />
              <h3 className="cd-display" style={{ fontSize: 20, fontWeight: 700, color: "#fff", marginBottom: 8 }}>No Active Jobs</h3>
              <p style={{ fontSize: 14, color: "rgba(255,255,255,.55)" }}>
                When you have confirmed bookings, they will appear here for communication.
              </p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {filteredJobs.map((job) => {
                const daysUntil = getDaysUntil(job.date);
                const isToday = daysUntil === 0;
                const isUpcoming = daysUntil > 0;
                const dateInfo = formatDate(job.date);
                const badge = cdStatusBadge[job.status] || { color: "#A8AEBC", bg: "rgba(255,255,255,.08)", dot: false };

                // Countdown rail styling per state
                let stripBg = "rgba(255,255,255,.04)";
                let numColor = "rgba(255,255,255,.6)";
                if (isToday) { stripBg = "rgba(185,28,28,.14)"; numColor = "#F87171"; }
                else if (isUpcoming) { stripBg = "rgba(103,198,180,.1)"; numColor = "#67C6B4"; }

                return (
                  <div
                    key={job.id}
                    onClick={() => navigate(`/job-comms/${job.id}`)}
                    className="cd-card cd-col-mobile"
                    style={{
                      cursor: "pointer",
                      ...(isToday ? { background: "rgba(103,198,180,.06)", borderColor: "rgba(103,198,180,.35)" } : null),
                    }}
                  >
                    {/* Countdown rail */}
                    <div className="cd-strip" style={{ flex: "0 0 100px", background: stripBg }}>
                      {daysUntil === null ? (
                        <span className="cd-strip-num" style={{ fontSize: 22, color: "rgba(255,255,255,.6)" }}>TBD</span>
                      ) : isToday ? (
                        <span className="cd-display" style={{ fontWeight: 800, fontSize: 24, color: "#F87171", lineHeight: 1 }}>TODAY</span>
                      ) : (
                        <>
                          <span className="cd-strip-num" style={{ color: numColor }}>{Math.abs(daysUntil)}</span>
                          <span className="cd-strip-label" style={{ color: numColor }}>
                            {isUpcoming
                              ? (daysUntil === 1 ? "DAY" : "DAYS")
                              : (Math.abs(daysUntil) === 1 ? "DAY AGO" : "DAYS AGO")}
                          </span>
                        </>
                      )}
                    </div>

                    {/* Card content */}
                    <div className="cd-w-full-m" style={{ flex: 1, minWidth: 0, padding: "18px 20px", display: "flex", alignItems: "center", gap: 18 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Meta row */}
                        <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap", marginBottom: 7 }}>
                          <span className="cd-badge" style={{ color: badge.color, background: badge.bg }}>
                            {badge.dot && <span className="cd-badge-dot" style={{ background: badge.color }} />}
                            {statusLabels[job.status] || job.status}
                          </span>
                          <span className="cd-mono" style={{ fontSize: 11, color: "rgba(255,255,255,.5)" }}>
                            {dateInfo.dayOfWeek ? `${dateInfo.dayOfWeek}, ` : ""}{dateInfo.fullDate}
                          </span>
                          <span style={{ fontSize: 12, color: "rgba(255,255,255,.55)" }}>
                            {job.user_role === "crew" ? "Client:" : "Crew:"} {job.other_party_name || "Unknown"}
                          </span>
                        </div>

                        {/* Title */}
                        <div className="cd-display" style={{ fontWeight: 700, fontSize: 18, color: "#fff" }}>
                          {job.title}
                        </div>

                        {/* Indicators row */}
                        <div style={{ display: "flex", gap: 14, marginTop: 9, flexWrap: "wrap", color: "rgba(255,255,255,.55)", fontSize: 12.5, alignItems: "center" }}>
                          {job.location_address && (
                            <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                              <MapPin style={{ width: 14, height: 14, flexShrink: 0 }} />
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{job.location_address}</span>
                            </span>
                          )}
                          {job.unread_count > 0 && (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "#C98AD4", fontWeight: 600 }}>
                              <MessageSquare style={{ width: 13, height: 13 }} />
                              {job.unread_count} unread
                            </span>
                          )}
                          {job.document_count > 0 && (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                              <FileText style={{ width: 13, height: 13 }} />
                              {job.document_count} {job.document_count === 1 ? "doc" : "docs"}
                            </span>
                          )}
                          {job.last_activity && (
                            <span style={{ color: "rgba(255,255,255,.4)" }}>{timeAgo(job.last_activity)}</span>
                          )}
                        </div>
                      </div>

                      <ChevronRight style={{ width: 20, height: 20, flexShrink: 0, color: "rgba(255,255,255,.5)" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobComms;
